import {
  Box,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { MdOutlineKeyboardArrowDown, MdFilterList } from 'react-icons/md';
import { RiCheckLine } from 'react-icons/ri';
import { useAuth } from '../../contexts/AuthContext';
import { DtType } from '../../generated/graphql';
import { getIndustryDetails } from '../../utils/industryDetails';
import { HelivanColor } from '../helivan/HelivanColor';

interface ProjectFilterProps {
  setSelectedTypes: (types: DtType[]) => void;
  selectedTypes: DtType[];
  type: 'project' | 'dt';
}

export const ProjectFilter = ({
  selectedTypes,
  setSelectedTypes,
  type,
}: ProjectFilterProps) => {
  const { backendUser } = useAuth();
  const typeOptions = (
    backendUser?.currentOrganization?.personaAccessTypes ?? []
  ).map((access) => {
    return {
      value: access,
      label: getIndustryDetails(access).industryDetails.title,
    };
  });

  const handleTypeToggle = (value: DtType) => {
    if (selectedTypes.includes(value)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== value));
    } else {
      setSelectedTypes([...selectedTypes, value]);
    }
  };

  const clearFilters = () => {
    setSelectedTypes([]);
  };

  if (typeOptions.length <= 1) {
    return null;
  }

  return (
    <Menu isLazy closeOnSelect={false}>
      <MenuButton
        as={Box}
        px={3}
        py={2}
        borderRadius="6px"
        bg="white"
        border="1px solid"
        borderColor={selectedTypes.length > 0 ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
        cursor="pointer"
        _hover={{ borderColor: HelivanColor.text.helivanBlue }}
        transition="all 0.15s ease"
      >
        <HStack spacing={2}>
          <MdFilterList size={16} color={selectedTypes.length > 0 ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid} />
          <Text
            fontSize="13px"
            color={selectedTypes.length > 0 ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
            fontWeight={selectedTypes.length > 0 ? '500' : '400'}
          >
            {selectedTypes.length > 0 ? `Filter (${selectedTypes.length})` : 'Filter'}
          </Text>
          <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList
          zIndex={1400}
          boxShadow="0 4px 20px rgba(0,0,0,0.12)"
          borderRadius="8px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          py={2}
          minWidth="180px"
        >
          <Box px={3} pb={2} mb={2} borderBottom="1px solid" borderBottomColor={HelivanColor.border.primary}>
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="12px" fontWeight="600" color={HelivanColor.text.blueDark}>
                {type === 'project' ? 'Project Type' : 'Persona Type'}
              </Text>
              {selectedTypes.length > 0 && (
                <Text
                  fontSize="11px"
                  color={HelivanColor.text.helivanBlue}
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={clearFilters}
                >
                  Clear
                </Text>
              )}
            </Flex>
          </Box>
          {typeOptions.map((option) => {
            const isSelected = selectedTypes.includes(option.value);
            return (
              <MenuItem
                key={option.value}
                onClick={() => handleTypeToggle(option.value)}
                fontSize="13px"
                py={2}
                px={3}
                bg={isSelected ? HelivanColor.hover.lightBlue : 'white'}
                _hover={{ bg: HelivanColor.background.secondary }}
              >
                <HStack spacing={2} width="full" justify="space-between">
                  <Text color={HelivanColor.text.blueDark}>{option.label}</Text>
                  {isSelected && (
                    <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />
                  )}
                </HStack>
              </MenuItem>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default ProjectFilter;
